import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from 'react-redux';

import { searchBarcode, fetchItems } from '../api/items';
import { addItem } from '../store/cartSlice';

/**
 * Search input with debounced typeahead and barcode scan detection.
 *
 * Barcode detection: if Enter is pressed, try barcode lookup first.
 * If a barcode match is found, add the item directly to the cart
 * and clear the input.
 */
export default function ItemSearch({ searchTerm, onSearchChange, posProfile, priceList, inputRef }) {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState(searchTerm);
  const debounceRef = useRef(null);

  // Debounce: update the parent search term after 300ms of no typing
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      onSearchChange(inputValue);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [inputValue, onSearchChange]);

  // Handle Enter key — try barcode lookup
  const handleKeyDown = async (e) => {
    if (e.key !== 'Enter' || !inputValue.trim()) return;
    e.preventDefault();

    try {
      const result = await searchBarcode(inputValue.trim());
      if (result?.item_code) {
        // Barcode matched — fetch the item details then add to cart
        const items = await fetchItems({
          searchTerm: result.item_code,
          posProfile,
          priceList,
          pageLength: 1,
        });
        if (items.length > 0) {
          dispatch(addItem(items[0]));
          setInputValue('');
          onSearchChange('');
          return;
        }
      }
    } catch {
      // Barcode lookup failed — fall through to normal search
    }

    // No barcode match — trigger normal search
    onSearchChange(inputValue);
  };

  return (
    <Box sx={{ p: 1.5, pb: 0 }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search items or scan barcode... ( / )"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        inputRef={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}
